import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  BotMessageSquare,
  CheckCircle2,
  FlaskConical,
  LoaderCircle,
  MessageSquareReply,
  Plus,
  Save,
} from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { Badge } from '../../components/ui/badge'
import { Button } from '../../components/ui/button'
import { Card } from '../../components/ui/card'
import { Input } from '../../components/ui/input'
import { Skeleton } from '../../components/ui/skeleton'
import { useToast } from '../../components/ui/toast'
import { api } from '../../lib/api'
import { relativeTime } from '../../lib/format'
import type { AutoReplyRule, AutoReplyTestResult } from '../../types'

const textareaClass =
  'min-h-28 w-full resize-y rounded-xl border border-[var(--border)] bg-[var(--surface)] px-3.5 py-3 text-base leading-6 text-[var(--foreground)] shadow-sm outline-none transition placeholder:text-[var(--muted-foreground)] focus:border-[var(--primary)] focus:ring-3 focus:ring-[var(--primary-soft)]'

function Field({
  label,
  hint,
  children,
}: {
  label: string
  hint?: string
  children: React.ReactNode
}) {
  return (
    <label className="flex flex-col gap-2 text-sm font-semibold">
      {label}
      {children}
      {hint && (
        <span className="text-xs font-normal leading-5 text-[var(--muted-foreground)]">{hint}</span>
      )}
    </label>
  )
}

const newRule = (): AutoReplyRule => {
  const now = new Date().toISOString()
  return {
    id: '',
    key: `custom_${Date.now()}`,
    label: 'Nuova risposta',
    identifier: '#NUOVA',
    exactPhrase: '',
    responseText: '',
    enabled: false,
    priority: 50,
    cooldownMinutes: 1440,
    executionCount: 0,
    createdAt: now,
    updatedAt: now,
  }
}

export function AutoReplySettingsPanel() {
  const queryClient = useQueryClient()
  const { showToast } = useToast()
  const settings = useQuery({
    queryKey: ['auto-reply-settings'],
    queryFn: api.autoReplySettings,
  })
  const [selectedKey, setSelectedKey] = useState<string>()
  const [editing, setEditing] = useState<AutoReplyRule>()
  const [testText, setTestText] = useState('')
  const [testResult, setTestResult] = useState<AutoReplyTestResult>()

  const selected = useMemo(
    () => settings.data?.rules.find((rule) => rule.key === selectedKey),
    [selectedKey, settings.data?.rules],
  )

  useEffect(() => {
    if (!selectedKey && settings.data?.rules[0]) setSelectedKey(settings.data.rules[0].key)
  }, [selectedKey, settings.data?.rules])

  useEffect(() => {
    if (selected) setEditing({ ...selected })
  }, [selected])

  const save = useMutation({
    mutationFn: (rule: AutoReplyRule) => api.saveAutoReplyRule(rule),
    onSuccess: (rule) => {
      setSelectedKey(rule.key)
      showToast(`Risposta “${rule.label}” salvata`)
      void queryClient.invalidateQueries({ queryKey: ['auto-reply-settings'] })
    },
    onError: (error) =>
      showToast(error instanceof Error ? error.message : 'Salvataggio fallito', 'error'),
  })
  const test = useMutation({
    mutationFn: api.testAutoReply,
    onSuccess: setTestResult,
    onError: (error) =>
      showToast(error instanceof Error ? error.message : 'Prova non riuscita', 'error'),
  })

  const submit = () => {
    if (!editing) return
    if (!/^#[A-Z0-9_-]{2,30}$/i.test(editing.identifier.trim())) {
      showToast('Usa un identificatore come #INFOPEM o #INFOMAURO', 'error')
      return
    }
    if (!editing.exactPhrase.trim()) {
      showToast('Inserisci la frase completa da riconoscere', 'error')
      return
    }
    if (editing.enabled && !editing.responseText.trim()) {
      showToast('Scrivi la risposta prima di attivare la regola', 'error')
      return
    }
    save.mutate({
      ...editing,
      identifier: editing.identifier.trim().toUpperCase(),
      label: editing.label.trim(),
      exactPhrase: editing.exactPhrase.trim(),
      responseText: editing.responseText.trim(),
    })
  }

  if (settings.isLoading) {
    return (
      <div className="mt-10 grid gap-5 lg:grid-cols-[280px_minmax(0,1fr)]">
        <Skeleton className="h-80" />
        <Skeleton className="h-[560px]" />
      </div>
    )
  }

  return (
    <section className="mt-10" aria-labelledby="auto-reply-title">
      <header className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.16em] text-[var(--primary)]">
            Automazioni conversazionali
          </p>
          <h2 id="auto-reply-title" className="mt-2 text-2xl font-semibold tracking-tight">
            Risposte automatiche
          </h2>
          <p className="mt-2 max-w-3xl text-sm leading-6 text-[var(--muted-foreground)]">
            Ogni regola cerca prima l’identificatore univoco, poi la frase completa ignorando
            maiuscole, accenti, punteggiatura e spazi. Nessuna ricerca approssimativa.
          </p>
        </div>
        <Button
          variant="secondary"
          onClick={() => {
            const rule = newRule()
            setSelectedKey(rule.key)
            setEditing(rule)
          }}
        >
          <Plus className="size-4" />
          Nuova regola
        </Button>
      </header>

      <div className="mt-6 grid gap-5 lg:grid-cols-[280px_minmax(0,1fr)]">
        <Card className="h-fit p-3">
          <div className="flex flex-col gap-2">
            {settings.data?.rules.map((rule) => (
              <button
                key={rule.key}
                type="button"
                onClick={() => setSelectedKey(rule.key)}
                className={`flex min-h-20 w-full items-center gap-3 rounded-xl p-3 text-left transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ring)] ${
                  selectedKey === rule.key ? 'bg-[var(--primary-soft)]' : 'hover:bg-[var(--muted)]'
                }`}
              >
                <span className="grid size-10 shrink-0 place-items-center rounded-xl bg-[var(--surface)] text-[var(--primary)] shadow-sm">
                  <MessageSquareReply className="size-5" />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-semibold">{rule.label}</span>
                  <span className="mt-1 flex items-center gap-2 text-xs text-[var(--muted-foreground)]">
                    <code>{rule.identifier}</code>
                    <span aria-hidden="true">·</span>
                    {rule.enabled ? 'Attiva' : 'Disattivata'}
                  </span>
                </span>
                <span
                  className={`size-2.5 shrink-0 rounded-full ${
                    rule.enabled ? 'bg-[var(--success)]' : 'bg-[var(--border)]'
                  }`}
                  aria-label={rule.enabled ? 'Regola attiva' : 'Regola disattivata'}
                />
              </button>
            ))}
          </div>
        </Card>

        {editing ? (
          <div className="flex flex-col gap-5">
            <Card className="p-4 sm:p-6">
              <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                <div className="flex items-center gap-3">
                  <span className="grid size-11 place-items-center rounded-xl bg-[var(--primary-soft)] text-[var(--primary)]">
                    <BotMessageSquare className="size-5" />
                  </span>
                  <div>
                    <h3 className="font-semibold">Configurazione indipendente</h3>
                    <p className="mt-1 text-xs text-[var(--muted-foreground)]">
                      {editing.executionCount} risposte inviate · ultima{' '}
                      {relativeTime(editing.lastExecutedAt)}
                    </p>
                  </div>
                </div>
                <Badge
                  className={
                    editing.enabled ? 'bg-[var(--success-soft)] text-[var(--success)]' : undefined
                  }
                >
                  {editing.enabled ? 'Attiva' : 'Bozza'}
                </Badge>
              </div>

              <div className="mt-6 grid gap-4 sm:grid-cols-2">
                <Field label="Nome della regola">
                  <Input
                    value={editing.label}
                    maxLength={120}
                    onChange={(event) =>
                      setEditing((current) =>
                        current ? { ...current, label: event.target.value } : current,
                      )
                    }
                  />
                </Field>
                <Field
                  label="Identificatore prioritario"
                  hint="Deve essere univoco. Esempi: #INFOPEM, #INFOTUTELA, #INFODAVID, #INFOMAURO."
                >
                  <Input
                    value={editing.identifier}
                    maxLength={31}
                    autoCapitalize="characters"
                    onChange={(event) =>
                      setEditing((current) =>
                        current ? { ...current, identifier: event.target.value } : current,
                      )
                    }
                  />
                </Field>
              </div>

              <div className="mt-4">
                <Field
                  label="Frase completa alternativa"
                  hint="Usata solo se l’identificatore manca. Il confronto è esatto dopo la normalizzazione."
                >
                  <textarea
                    className={textareaClass}
                    value={editing.exactPhrase}
                    maxLength={1000}
                    onChange={(event) =>
                      setEditing((current) =>
                        current ? { ...current, exactPhrase: event.target.value } : current,
                      )
                    }
                  />
                </Field>
              </div>

              <div className="mt-4">
                <Field
                  label="Risposta da inviare"
                  hint="Messaggio testuale inviato nella finestra WhatsApp aperta dal visitatore."
                >
                  <textarea
                    className={`${textareaClass} min-h-44`}
                    value={editing.responseText}
                    maxLength={4096}
                    placeholder="Scrivi qui la risposta automatica…"
                    onChange={(event) =>
                      setEditing((current) =>
                        current ? { ...current, responseText: event.target.value } : current,
                      )
                    }
                  />
                </Field>
              </div>

              <div className="mt-4 grid gap-4 sm:grid-cols-2">
                <Field
                  label="Pausa per lo stesso contatto (minuti)"
                  hint="1440 equivale a 24 ore. Usa 0 per rispondere sempre."
                >
                  <Input
                    type="number"
                    min={0}
                    max={43200}
                    value={editing.cooldownMinutes}
                    onChange={(event) =>
                      setEditing((current) =>
                        current
                          ? { ...current, cooldownMinutes: Number(event.target.value) }
                          : current,
                      )
                    }
                  />
                </Field>
                <Field
                  label="Priorità"
                  hint="Conta solo se più regole potrebbero riconoscere lo stesso messaggio."
                >
                  <Input
                    type="number"
                    min={0}
                    max={1000}
                    value={editing.priority}
                    onChange={(event) =>
                      setEditing((current) =>
                        current ? { ...current, priority: Number(event.target.value) } : current,
                      )
                    }
                  />
                </Field>
              </div>

              <div className="mt-5 flex flex-col gap-4 rounded-xl border border-[var(--border)] bg-[var(--surface-elevated)] p-4 sm:flex-row sm:items-center sm:justify-between">
                <label className="flex items-center gap-3 text-sm font-semibold">
                  <input
                    type="checkbox"
                    checked={editing.enabled}
                    onChange={(event) =>
                      setEditing((current) =>
                        current ? { ...current, enabled: event.target.checked } : current,
                      )
                    }
                    className="size-5 accent-[var(--primary)]"
                  />
                  Attiva questa risposta automatica
                </label>
                <Button disabled={save.isPending} onClick={submit}>
                  {save.isPending ? (
                    <LoaderCircle className="size-4 animate-spin" />
                  ) : editing.enabled ? (
                    <CheckCircle2 className="size-4" />
                  ) : (
                    <Save className="size-4" />
                  )}
                  Salva regola
                </Button>
              </div>
            </Card>

            <Card className="p-4 sm:p-6">
              <div className="flex items-center gap-3">
                <FlaskConical className="size-5 text-[var(--primary)]" />
                <div>
                  <h3 className="font-semibold">Prova il riconoscimento</h3>
                  <p className="mt-1 text-sm leading-6 text-[var(--muted-foreground)]">
                    Non invia nulla su WhatsApp: mostra soltanto quale regola attiva risponde.
                  </p>
                </div>
              </div>
              <div className="mt-4 flex flex-col gap-3 sm:flex-row">
                <Input
                  value={testText}
                  placeholder={`Incolla un messaggio, per esempio ${editing.identifier}`}
                  onChange={(event) => {
                    setTestText(event.target.value)
                    setTestResult(undefined)
                  }}
                />
                <Button
                  variant="secondary"
                  disabled={!testText.trim() || test.isPending}
                  onClick={() => test.mutate(testText)}
                >
                  {test.isPending ? (
                    <LoaderCircle className="size-4 animate-spin" />
                  ) : (
                    <FlaskConical className="size-4" />
                  )}
                  Verifica
                </Button>
              </div>
              {testResult && (
                <div
                  aria-live="polite"
                  className={`mt-4 rounded-xl border p-4 text-sm leading-6 ${
                    testResult.matched
                      ? 'border-[var(--success-border)] bg-[var(--success-soft)] text-[var(--success)]'
                      : 'border-[var(--warning-border)] bg-[var(--warning-soft)] text-[var(--warning)]'
                  }`}
                >
                  {testResult.matched
                    ? `Riconosciuta “${testResult.rule?.label}” tramite ${
                        testResult.kind === 'identifier' ? 'identificatore' : 'frase completa'
                      }.`
                    : 'Nessuna regola attiva riconosce questo messaggio.'}
                </div>
              )}
            </Card>
          </div>
        ) : (
          <Card className="grid min-h-80 place-items-center p-8 text-center">
            <div>
              <MessageSquareReply className="mx-auto size-10 text-[var(--muted-foreground)]" />
              <p className="mt-4 font-semibold">Nessuna regola selezionata</p>
            </div>
          </Card>
        )}
      </div>
    </section>
  )
}
